import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { HistoryField } from 'components/Field/HistoryField';
import { ReviewStatusField } from 'components/Field/ReviewStatusField';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { ImageDisplay } from 'componentsAware/ImageDisplay';
import { useConfirm } from 'hooks/useConfirm';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { randomKey, rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { pick } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  eventAseSelectFindingQuestion,
  NAVIGATE_MODAL,
} from 'store/app/actions';
import {
  doAseDeleteControl,
  doAseOpenControl,
  doAseReviewControl,
  doAseUpdateControl,
} from 'store/assessments/actions';
import {
  selectAseCategories,
  selectAseControlStatuses,
  selectAseIsEditable,
  selectAseControlAstAse,
  selectAseIsOnline,
  selectAseContext,
} from 'store/assessments/selectors';
import { returnDefaultControl } from 'store/assessments/templates';
import { AssessmentItemTypes } from 'store/assessments/types';
import { appContextToFileContext } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_CONTROL
>;

export const ControlDisplay: React.FC<{
  navigation: Pick<Props['navigation'], 'goBack'>;
  route: Pick<Props['route'], 'params'>;
}> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const screenSize = useScreenSize();
  const { id, parent } = route.params;

  const context = useSelector(selectAseContext)!;
  const selectedControl = useSelector((state) =>
    selectAseControlAstAse(state, id),
  );

  const control =
    selectedControl ||
    returnDefaultControl({
      sectionId: '1',
      questionId: null,
      fieldInstanceId: null,
      suggestionId: null,
    });

  const editable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isQc = editable && isOnline;

  const controlStatuses = useSelector(selectAseControlStatuses);
  const categories = useSelector(selectAseCategories);

  const descriptionValue = control.description || 'No Description';
  const whyValue = control.why || null;
  const locationValue = control.where || null;
  const category = categories[control.categoryId] ?? categories['0']!;
  const imageIds = control.photoIds;

  const handleReviewStatusChange = (reviewStatusId: string) => {
    dispatch(
      doAseReviewControl({
        id,
        parent,
        reviewStatusId,
      }),
    );
  };

  const handleEditPress = () =>
    dispatch(
      doAseOpenControl({
        id: control.id,
        parent,
        edit: true,
      }),
    );

  const handleDelete = () =>
    confirm({
      text: control.isReview
        ? 'Are you sure you want to delete the review for this control?'
        : 'Are you sure you want to delete this control?',
      title: control.isReview ? 'Delete Review' : 'Delete Control',
      confirmText: 'Delete',
      onConfirm: () => dispatch(doAseDeleteControl({ id: control.id })),
      destructive: true,
    });

  const handleDuplicatePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseControl,
        params: {
          id: '',
          isDuplicate: true,
          parent,
          data: {
            ...returnDefaultControl({
              questionId: control.questionId,
              sectionId: control.sectionId,
              fieldInstanceId: control.fieldInstanceId,
              suggestionId: control.suggestionId,
            }),
            ...pick(control, ['description', 'why', 'where', 'categoryId']),
          },
        },
      }),
    );

  // const handleSuggestTemplate = () =>
  //   !control.questionId
  //     ? dispatch(
  //         ALERT({
  //           text:
  //             'Controls cannot be suggested for sections. ' +
  //             'Move this control to a question or checklist before turning it into a suggestion',
  //         }),
  //       )
  //     : dispatch(
  //         NAVIGATE_MODAL({
  //           screen: ModalScreens.AseMakeSuggestion,
  //           params: {
  //             type: 'control',
  //             description: control.description,
  //             why: control.why,
  //             categoryId: category.id,
  //             severityId: '0',
  //             timescaleId: '0',
  //             dueDeterminedBy: DueDeterminedBy.timescaleBased,
  //             dueTimescale: null,
  //             dueTypeId: '',
  //             questionId: control.questionId,
  //             answerId: answerId || undefined,
  //           },
  //         }),
  //       );

  const selectQuestionEventKey = React.useRef(randomKey()).current;
  const handleMovePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASEFINDINGBELONGSTO,
        params: {
          findingType: AssessmentItemTypes.CONTROL,
          questionId: control.questionId,
          sectionId: control.sectionId,
          fieldInstanceId: control.fieldInstanceId,
          eventKey: selectQuestionEventKey,
        },
      }),
    );

  React.useEffect(() => {
    const eventSelectFindingQuestion = doAddEvent(
      eventAseSelectFindingQuestion,
      (event) => {
        if (event.payload.key === selectQuestionEventKey) {
          dispatch(
            doAseUpdateControl({
              id,
              data: { ...control, ...event.payload.data },
            }),
          );
        }
      },
    );
    return () => {
      doRemoveEvent(eventSelectFindingQuestion);
    };
  }, [control, dispatch, id, selectQuestionEventKey]);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Control"
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
          onPressSaveButton={
            isQc && (!control.isReview || !!control.reviewStatusId)
              ? handleEditPress
              : undefined
          }
          saveButtonText="Edit"
          menuRight={
            isQc && (!control.isReview || !!control.reviewStatusId)
              ? {
                  iconButtonConfig: {
                    icon: ICONS.EllipsisLight,
                  },
                  options: [
                    {
                      text: 'Move',
                      onPress: handleMovePress,
                      icon: ICONS.HandLight,
                    },
                    {
                      text: 'Duplicate',
                      onPress: handleDuplicatePress,
                      icon: ICONS.CopyLight,
                    },
                    // {
                    //   text: 'Suggest',
                    //   icon: ICONS.LightbulbOnLight,
                    //   onPress: handleSuggestTemplate,
                    // },
                    '-',
                    {
                      text: control.isReview
                        ? 'Delete Review'
                        : 'Delete Control',
                      onPress: handleDelete,
                      icon: ICONS.TrashCanLight,
                      destructive: true,
                    },
                  ],
                }
              : undefined
          }
        />
      }
    >
      <Card>
        <ViewWithSeparators horizontal style={{ flex: 1 }}>
          <CardRow
            icon={ICONS.RBPlectrumSolid}
            label={
              control.reference
                ? rbAddReferencePrefix(control.reference)
                : undefined
            }
            style={{ flex: 1 }}
          >
            {!control.reference && (
              <Txt>
                {rbAddReferencePrefix('')}
                <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
              </Txt>
            )}
          </CardRow>
          <HistoryField
            history={control.history}
            onPress={() =>
              dispatch(
                NAVIGATE_MODAL({
                  screen: ModalScreens.ASEHISTORY,
                  params: {
                    id,
                    findingType: AssessmentItemTypes.CONTROL,
                  },
                }),
              )
            }
          />
        </ViewWithSeparators>
      </Card>

      {editable && control.isReview && !control.reviewStatusId ? (
        <Card label="Choose a Status to Start Review">
          <ReviewStatusField
            options={controlStatuses.map((status) => ({
              label: status.name,
              value: status.id,
            }))}
            value={control.reviewStatusId}
            onChange={handleReviewStatusChange}
          />
        </Card>
      ) : (
        !!control.reviewStatusId && (
          <Card>
            <CardRow
              labelAlignTop
              label="Reviewed As"
              value={
                controlStatuses.find(
                  ({ id: reviewStatusId }) =>
                    reviewStatusId === control.reviewStatusId,
                )?.name ?? 'Unknown Status'
              }
            />
            {!!control.reviewNote && (
              <CardRow
                labelAlignTop
                label="Review Note"
                value={control.reviewNote}
              />
            )}
          </Card>
        )
      )}

      <Card>
        <CardRow labelAlignTop label="Description" value={descriptionValue} />
        {!!whyValue && <CardRow labelAlignTop label="Why" value={whyValue} />}
        {!!locationValue && (
          <CardRow labelAlignTop label="Location" value={locationValue} />
        )}
      </Card>

      {category.id !== '0' && (
        <Card>
          {category.id !== '0' && (
            <CardRow
              icon={ICONS.TagLight}
              label="Category"
              value={category.name}
            />
          )}
        </Card>
      )}
      {!!imageIds.length && (
        <Card>
          <ImageDisplay
            value={imageIds}
            context={appContextToFileContext(context)}
          />
        </Card>
      )}
    </Page>
  );
};
